<template>
  <NativeDrawer
    v-model="drawerVisible"
    :title="isEdit ? '编辑新闻' : '新增新闻'"
    size="700px"
    direction="rtl"
    @close="handleClose"
    destroy-on-close
    :before-close="handleDrawerClose"
  >
    <div class="drawer-content">
      <NativeForm
        ref="formRef"
        :model="localFormData"
        :rules="formRules"
        label-width="100px"
        class="news-form"
      >
        <!-- 基本信息 -->
        <div class="form-section">
          <div class="section-title">
            <span class="native-icon" aria-hidden="true">•</span>
            <span>基本信息</span>
          </div>

          <NativeFormItem label="标题" prop="title">
            <NativeInput
              v-model="localFormData.title"
              placeholder="请输入新闻标题"
              clearable
            />
          </NativeFormItem>

          <NativeRow :gutter="20">
            <NativeCol :span="12">
              <NativeFormItem label="分类" prop="category">
                <NativeSelect
                  v-model="localFormData.category"
                  placeholder="请选择分类"
                  style="width: 100%"
                  clearable
                >
                  <NativeOption
                    v-for="value in categoryOptions"
                    :key="value.label"
                    :label="value.value"
                    :value="value.value"
                  />
                </NativeSelect>
              </NativeFormItem>
            </NativeCol>
            <NativeCol :span="12">
              <NativeFormItem label="阅读量" prop="views">
                <NativeInput
                  v-model="localFormData.views"
                  placeholder="请输入阅读量"
                  clearable
                />
              </NativeFormItem>
            </NativeCol>
          </NativeRow>

          <NativeRow :gutter="20">
            <NativeCol :span="12">
              <NativeFormItem label="发布日期" prop="date">
                <NativeInput
                  v-model="localFormData.date"
                  type="date"
                  placeholder="选择日期"
                  style="width: 100%"
                />
              </NativeFormItem>
            </NativeCol>
            <NativeCol :span="12">
              <NativeFormItem label="状态" prop="status">
                <NativeSelect
                  v-model="localFormData.status"
                  placeholder="请选择状态"
                  style="width: 100%"
                >
                  <NativeOption label="已下架" :value="0" />
                  <NativeOption label="已发布" :value="1" />
                </NativeSelect>
              </NativeFormItem>
            </NativeCol>
          </NativeRow>

          <NativeFormItem label="封面图" prop="image">
            <NativeInput
              v-model="localFormData.image"
              placeholder="请输入封面图URL"
              clearable
            />
          </NativeFormItem>
        </div>

        <!-- 详细内容 -->
        <div class="form-section">
          <div class="section-title">
            <span class="native-icon" aria-hidden="true">•</span>
            <span>详细内容</span>
          </div>

          <NativeFormItem label="摘要" prop="excerpt">
            <NativeInput
              v-model="localFormData.excerpt"
              type="textarea"
              :rows="3"
              placeholder="请输入新闻摘要，建议50-200字"
              show-word-limit
            />
          </NativeFormItem>

          <NativeFormItem label="内容" prop="content">
            <NativeInput
              v-model="localFormData.content"
              type="textarea"
              :rows="8"
              placeholder="请输入新闻内容"
            />
          </NativeFormItem>
        </div>
      </NativeForm>
    </div>

    <template #footer>
      <div class="drawer-footer">
        <NativeButton @click="handleCancel" size="default">
          <span class="native-icon" aria-hidden="true">×</span>
          <span>取消</span>
        </NativeButton>
        <NativeButton
          type="primary"
          @click="handleSubmit"
          :loading="submitLoading"
          size="default"
        >
          <span class="native-icon" aria-hidden="true">✓</span>
          <span>确定</span>
        </NativeButton>
      </div>
    </template>
  </NativeDrawer>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import type {
  NativeFormInstance,
  NativeFormRules,
} from "@/components/NativeUI/types";
import type { NewsType } from "@/stores/news";

// Props
const props = defineProps<{
  visible: boolean;
  isEdit: boolean;
  formData: Partial<NewsType>;
  submitLoading: boolean;
}>();

// Emits
const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (
    e: "submit",
    data: {
      formData: Partial<NewsType>;
      originalData: Partial<NewsType>;
    },
  ): void;
  (e: "cancel"): void;
}>();

// 表单引用
const formRef = ref<NativeFormInstance>();

// Drawer 显示状态
const drawerVisible = ref(props.visible);

// 原始数据快照
const originalData = ref<Partial<NewsType>>({});

// 本地表单数据
const localFormData = ref<Partial<NewsType>>({
  id: 0,
  title: "",
  category: "",
  date: "",
  author: "",
  excerpt: "",
  content: "",
  image: "",
  status: 0,
  views: 0,
});

const categoryOptions = [
  { label: "医院新闻", value: "医院新闻" },
  { label: "门诊排班", value: "门诊排班" },
  { label: "医院动态", value: "医院动态" },
  { label: "医疗技术", value: "医疗技术" },
  { label: "荣誉奖项", value: "荣誉奖项" },
  { label: "通知", value: "通知" },
  { label: "招聘信息", value: "招聘信息" },
  { label: "健康讲座", value: "健康讲座" },
  { label: "学术", value: "学术" },
  { label: "科研", value: "科研" },
];

// 同步表单数据
const syncFormData = () => {
  originalData.value = JSON.parse(JSON.stringify(props.formData));
  localFormData.value = {
    ...props.formData,
  };
};

// 监听 visible 变化
watch(
  () => props.visible,
  (newVal) => {
    drawerVisible.value = newVal;
    if (newVal) {
      syncFormData();
    }
  },
);

// 监听 isEdit 变化
watch(
  () => props.isEdit,
  () => {
    if (drawerVisible.value) {
      syncFormData();
    }
  },
);

// 监听 drawerVisible 变化
watch(drawerVisible, (newVal) => {
  emit("update:visible", newVal);
});

// 表单验证规则
const formRules: NativeFormRules = {
  title: [
    { required: true, message: "请输入标题", trigger: "blur" },
    { min: 2, max: 100, message: "长度在 2 到 100 个字符", trigger: "blur" },
  ],
  category: [{ required: true, message: "请选择分类", trigger: "change" }],
  date: [{ required: true, message: "请选择发布日期", trigger: "change" }],
  status: [{ required: true, message: "请选择状态", trigger: "change" }],
  excerpt: [
    { required: true, message: "请输入摘要", trigger: "blur" },
    { min: 10, message: "摘要至少10个字符", trigger: "blur" },
  ],
  content: [{ required: true, message: "请输入内容", trigger: "blur" }],
};

// 提交
const handleSubmit = async () => {
  if (!formRef.value) return;

  await formRef.value.validate((valid) => {
    if (valid) {
      const data = { ...localFormData.value };
      emit("submit", {
        formData: data,
        originalData: originalData.value,
      });
    }
  });
};

const handleDrawerClose = (done: () => void) => {
  if (window.confirm("确认是否关闭?")) done();
};

// 取消
const handleCancel = () => {
  originalData.value = {};
  emit("cancel");
  drawerVisible.value = false;
};

// 关闭抽屉
const handleClose = () => {
  originalData.value = {};
  emit("cancel");
};
</script>

<style scoped lang="scss">
.drawer-content {
  height: calc(100% - 60px);
  overflow-y: auto;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #dcdfe6;
    border-radius: 3px;

    &:hover {
      background-color: #c0c4cc;
    }
  }
}

.news-form {
  padding: 0 10px;

  .form-section {
    margin-bottom: 24px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;

    &:last-child {
      margin-bottom: 0;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 2px solid #dee2e6;
      font-size: 15px;
      font-weight: 600;
      color: #343a40;

      .native-icon {
        font-size: 18px;
        color: #409eff;
      }
    }
  }
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 8px 0;
}

.native-form-item {
  margin-bottom: 18px;
}
</style>
